import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayerTypes } from '@redux/map/types';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { useDispatch } from 'react-redux';
import { changedLayerVisibility, updatedMapboxLayersOrder } from '@redux/map/map-reducer';
import './layers-list.scss';
import { useTranslation } from 'react-i18next';
import {
    selectAllLayersById,
    selectAllLayersIdsBySourceId,
    selectLayersGroups,
    selectListLayers,
} from '@redux/selectors/selectors';
import LoadingSpinner from '@components/Spinner/LoadingSpinner';
import { isLayerVisibleInList, isLayerVisibleOnMap } from '@utils/mapUtils';
import { useTheme } from '@context/themeContext';
import { AlertRhombusIcon } from '@components/icons/icons/AlertRhombusIcon';
import Tooltip from '@mui/material/Tooltip';
import { closestCenter, DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core/dist/types';
import LayersListGroup from './LayersListGroup';
import DraggableItem from '@components/DraggableItem/DraggableItem';
import { isUuidString } from '@utils/utils';
import { themeColors } from '@styles/theme';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import { LockClosedIcon } from '@components/icons/icons/LockClosedIcon';
import { LockOpenedIcon } from '@components/icons/icons/LockOpenedIcon';
import { useMapLayersClickabilityContext } from '@context/mapLayersClickability';
import NewLayerFileUploader from '../new-layer-file-uploader/NewLayerFileUploader';
import { NewLayerFromPreset } from '../new-layer-from-preset/ui/NewLayerFromPreset';

type RowProps<T> = {
    rowItem: T;
    visible: boolean;
    dayTimeDependent?: boolean;
    draggable?: boolean;

    style?: any;
    attributes?: any;
    listeners?: any;
};

type LayerRowProps = RowProps<LayerTypes>;

export const LayerRow = React.memo(
    React.forwardRef(
        (
            { rowItem, visible, dayTimeDependent, ...rest }: LayerRowProps,
            ref: React.ForwardedRef<HTMLDivElement>
        ) => {
            const mounted = useRef<boolean>(false);
            const layersIdsBySourceId = useTypedSelector(selectAllLayersIdsBySourceId);
            const { lockLayer } = useMapLayersClickabilityContext();
            const dispatch = useDispatch();
            const { t } = useTranslation();
            const { theme } = useTheme();

            const { id, source, layerName, clickable, alwaysLocked, loading, isLockBtnVisible } =
                rowItem;

            const lockIconColor = useMemo(
                () =>
                    theme === 'light'
                        ? clickable
                            ? themeColors.navyBlue
                            : themeColors.grey3
                        : clickable
                          ? themeColors.white
                          : themeColors.grey3,
                [clickable]
            );
            const lockIconBgColor = theme === 'light' ? themeColors.lightGrey : themeColors.grey4;

            useEffect(() => {
                if (mounted.current) {
                    dispatch(changedLayerVisibility({ layerId: id, visible }));
                } else {
                    mounted.current = true;
                }
            }, [visible]);

            const changeVisibility = useCallback(
                ({ target }) => {
                    layersIdsBySourceId[source].map(id => {
                        dispatch(changedLayerVisibility({ layerId: id, visible: target.checked }));
                    });
                },
                [source, changedLayerVisibility, layersIdsBySourceId]
            );

            return (
                <div
                    id={id}
                    className="layer-row-item layer-row-item_colored"
                    ref={ref}
                    data-testid="layersListRow"
                    {...rest}
                >
                    <div className="row-check">
                        {loading ? (
                            <LoadingSpinner size="13px" backgroundColor={'transparent'} />
                        ) : (
                            <input type="checkbox" checked={visible} onChange={changeVisibility} />
                        )}
                    </div>
                    <div className="row-name" draggable>
                        <span>{layerName}</span>
                    </div>
                    <div className="layer-row-item__controls">
                        {dayTimeDependent ? (
                            <Tooltip
                                title={t('layers-list.daytime-dependent-layer-warning')}
                                placement="top"
                            >
                                <div className="alert">
                                    <AlertRhombusIcon />
                                </div>
                            </Tooltip>
                        ) : null}
                        {isLockBtnVisible ? (
                            <Tooltip
                                title={
                                    alwaysLocked
                                        ? t('layers-list.layer-always-locked-tooltip')
                                        : clickable
                                          ? t('layers-list.layer-lock-tooltip')
                                          : t('layers-list.layer-unlock-tooltip')
                                }
                                placement="top"
                            >
                                <IconButton
                                    icon={
                                        clickable && !alwaysLocked ? LockOpenedIcon : LockClosedIcon
                                    }
                                    iconColor={lockIconColor}
                                    backgroundColor={lockIconBgColor}
                                    width={24}
                                    height={24}
                                    onClick={() =>
                                        lockLayer({
                                            layerId: id,
                                            sourceId: source,
                                            isInteractive: !clickable && !alwaysLocked,
                                        })
                                    }
                                    disabled={alwaysLocked}
                                />
                            </Tooltip>
                        ) : null}
                    </div>
                </div>
            );
        }
    )
);

export const LayersList = () => {
    const [allLayersChecked, setAllLayersChecked] = useState<boolean>(false);
    const allLayersById = useTypedSelector(selectAllLayersById);
    const listLayers = useTypedSelector(selectListLayers);
    const layersGroups = useTypedSelector(selectLayersGroups);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        const allChecked = Object.values(allLayersById).every(layer => {
            return isLayerVisibleOnMap(layer);
        });

        setAllLayersChecked(allChecked);
    }, [allLayersById]);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10,
            },
        })
    );

    const allLayersSelectionHandler = ({ target }) => {
        Object.values(allLayersById).map(layer =>
            dispatch(
                changedLayerVisibility({
                    layerId: layer.id,
                    visible: target.checked,
                })
            )
        );
    };

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                let oldIndex = 0,
                    newIndex = 0;

                listLayers.layersIds.forEach((id, ind) => {
                    if (id === active.id) {
                        oldIndex = ind;
                    }
                    if (id === over.id) {
                        newIndex = ind;
                    }
                });

                dispatch(updatedMapboxLayersOrder({ oldIndex, newIndex }));
            }
        },
        [listLayers]
    );

    return (
        <div className={`layers-list-container`} data-testid="layersList">
            <DndContext
                sensors={sensors}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={listLayers.layersIds}
                    strategy={verticalListSortingStrategy}
                >
                    {listLayers.layersIds.some(id => isUuidString(id)) ? (
                        <>
                            <div className="layers-list-header">
                                <div className="row-check">
                                    <input
                                        type="checkbox"
                                        checked={allLayersChecked}
                                        onChange={allLayersSelectionHandler}
                                    />
                                </div>
                                <div className="row-name">
                                    <span>{t('layers-list.all-layers')}</span>
                                </div>
                                <div className="layers-list-header-controls">
                                    <NewLayerFileUploader />
                                    <NewLayerFromPreset />
                                </div>
                            </div>
                            {layersGroups.map((group, groupInd) => {
                                let noVisibleLayers = true;

                                const layersElements = listLayers.layersByGroup[group].map(
                                    (layer, ind) => {
                                        const { id, dayTimeDependent } = layer;

                                        if (!isLayerVisibleInList(layer, layer.state)) {
                                            return (
                                                <React.Fragment
                                                    key={`${layer.id}_${ind}`}
                                                ></React.Fragment>
                                            );
                                        }

                                        noVisibleLayers = false;

                                        return (
                                            <DraggableItem<LayerRowProps>
                                                key={`${id}_${ind}`}
                                                id={id}
                                                Component={LayerRow}
                                                props={{
                                                    rowItem: layer,
                                                    visible: isLayerVisibleOnMap(layer),
                                                    dayTimeDependent,
                                                }}
                                            />
                                        );
                                    }
                                );

                                return (
                                    <React.Fragment key={groupInd}>
                                        {noVisibleLayers ? null : (
                                            <LayersListGroup title={group}>
                                                {layersElements}
                                            </LayersListGroup>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </>
                    ) : (
                        t('map.no-layers-exist-warning')
                    )}
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default LayersList;
